import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DownloadSimple, UploadSimple, EnvelopeSimple, X, Plus } from "@phosphor-icons/react";

export default function Manutenzione() {
  const { user } = useAuth();
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const restoreRef = useRef(null);
  const [emailsOpen, setEmailsOpen] = useState(false);
  const [backupEmails, setBackupEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [savingEmails, setSavingEmails] = useState(false);

  if (!["admin", "master"].includes(user?.role))
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="manutenzione-forbidden">
        <p className="font-semibold">Accesso riservato agli amministratori.</p>
      </div>
    );

  const generaBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="(.+)"/);
      const filename = match ? match[1] : "risorice_backup.zip";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup generato e scaricato");
    } catch (e) {
      toast.error("Errore nella generazione del backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const ripristinaBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Il ripristino SOSTITUIRÀ tutti i dati attuali con quelli del backup. Continuare?")) return;
    setRestoreLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/restore", fd);
      toast.success(`${data.message}: ${Object.entries(data.ripristinati).map(([k, v]) => `${k} ${v}`).join(", ")}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRestoreLoading(false);
    }
  };

  const apriEmails = async () => {
    try {
      const { data } = await api.get("/settings");
      setBackupEmails(data.backup_emails || []);
    } catch (e) {
      setBackupEmails([]);
    }
    setNewEmail("");
    setEmailsOpen(true);
  };

  const aggiungiEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email.includes("@")) { toast.error("Indirizzo email non valido"); return; }
    if (backupEmails.includes(email)) { toast.error("Indirizzo già presente"); return; }
    setBackupEmails((l) => [...l, email]);
    setNewEmail("");
  };

  const salvaEmails = async () => {
    if (backupEmails.length === 0 && !window.confirm("Nessun destinatario: il backup settimanale via email verrà disattivato. Continuare?")) return;
    setSavingEmails(true);
    try {
      await api.put("/settings", { backup_emails: backupEmails });
      toast.success("Destinatari del backup salvati");
      setEmailsOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingEmails(false);
    }
  };

  const cards = [
    {
      icon: EnvelopeSimple, title: "Email Backup", testId: "backup-emails-button",
      desc: "Scegli gli indirizzi email a cui inviare automaticamente il backup ogni lunedì alle 06:00 UTC.",
      action: apriEmails, label: "Gestisci destinatari", loading: false,
    },
    {
      icon: UploadSimple, title: "Ripristina Backup", testId: "restore-button",
      desc: "Carica un file .zip di backup per ripristinare tutti i dati. I dati attuali verranno sostituiti.",
      action: () => restoreRef.current?.click(), label: restoreLoading ? "Ripristino..." : "Carica backup", loading: restoreLoading,
    },
    {
      icon: DownloadSimple, title: "Genera Backup", testId: "backup-button",
      desc: "Esporta subito l'intero database in un file .zip scaricabile (tutte le collezioni + info).",
      action: generaBackup, label: backupLoading ? "Generazione..." : "Scarica backup", loading: backupLoading,
    },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="manutenzione-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold">Manutenzione</h1>
        <p className="text-[#6B7280] mt-1">Backup, ripristino e invio automatico dei dati del gestionale.</p>
      </div>

      <input ref={restoreRef} type="file" accept=".zip" className="hidden" onChange={ripristinaBackup} data-testid="restore-file-input" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((c) => (
          <div key={c.title} className="bg-white border border-[#E5E5E0] rounded-2xl p-6 flex flex-col" data-testid={`manutenzione-card-${c.testId}`}>
            <div className="h-11 w-11 rounded-xl bg-[#FAF9F6] border border-[#E5E5E0] flex items-center justify-center mb-4">
              <c.icon size={24} weight="duotone" className="text-[#D95D39]" />
            </div>
            <p className="font-bold text-lg mb-1">{c.title}</p>
            <p className="text-sm text-[#6B7280] mb-6 flex-1">{c.desc}</p>
            <Button onClick={c.action} disabled={c.loading} data-testid={c.testId} variant="outline" className="rounded-full gap-2 hover:bg-stone-50 w-full">
              <c.icon size={18} /> {c.label}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={emailsOpen} onOpenChange={setEmailsOpen}>
        <DialogContent className="max-w-md" data-testid="backup-emails-dialog">
          <DialogHeader><DialogTitle>Destinatari backup settimanale</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#6B7280]">Ogni lunedì alle 06:00 UTC il backup del database viene inviato via email a questi indirizzi.</p>
            <div className="space-y-2">
              {backupEmails.length === 0 && <p className="text-sm text-[#6B7280] italic">Nessun destinatario configurato.</p>}
              {backupEmails.map((email, i) => (
                <div key={email} className="flex items-center justify-between bg-[#FAF9F6] border border-[#E5E5E0] rounded-xl px-4 py-2.5" data-testid={`backup-email-item-${i}`}>
                  <span className="text-sm font-medium truncate">{email}</span>
                  <button type="button" onClick={() => setBackupEmails((l) => l.filter((e) => e !== email))} data-testid={`backup-email-remove-${i}`} className="h-6 w-6 rounded-full flex items-center justify-center text-[#D32F2F] hover:bg-red-50 shrink-0">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="nuovo@indirizzo.it"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aggiungiEmail(); } }}
                data-testid="backup-email-input"
              />
              <Button variant="outline" onClick={aggiungiEmail} data-testid="backup-email-add" className="rounded-full gap-1.5 shrink-0 hover:bg-stone-50">
                <Plus size={14} /> Aggiungi
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailsOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={salvaEmails} disabled={savingEmails} data-testid="backup-emails-save" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">
              {savingEmails ? "Salvataggio..." : "Salva destinatari"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
